import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { notifications } from '@mantine/notifications';
import type { AssetLink } from '@/types/models';

// Fetch all links for a specific asset (as parent or child)
export function useAssetLinks(organizationId: string | undefined, assetId: string | undefined) {
  return useQuery({
    queryKey: ['asset-links', organizationId, assetId],
    queryFn: async () => {
      if (!organizationId || !assetId) throw new Error('Organization ID and Asset ID are required');

      // Fetch links where this asset is the parent
      const { data: childLinks, error: childError } = await supabase
        .from('asset_links')
        .select(`
          *,
          child_asset:assets!asset_links_child_id_fkey(
            id, description, tag_id, brand, model, status, photo_url
          )
        `)
        .eq('organization_id', organizationId)
        .eq('parent_id', assetId);

      if (childError) throw childError;

      // Fetch links where this asset is the child
      const { data: parentLinks, error: parentError } = await supabase
        .from('asset_links')
        .select(`
          *,
          parent_asset:assets!asset_links_parent_id_fkey(
            id, description, tag_id, brand, model, status, photo_url
          )
        `)
        .eq('organization_id', organizationId)
        .eq('child_id', assetId);

      if (parentError) throw parentError;

      return {
        children: childLinks as AssetLink[],
        parents: parentLinks as AssetLink[],
      };
    },
    enabled: !!organizationId && !!assetId,
  });
}

// Create a new asset link
export function useCreateAssetLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      parentId,
      childId,
      shouldGroup = false,
    }: {
      organizationId: string;
      parentId: string;
      childId: string;
      shouldGroup?: boolean;
    }) => {
      // Check if link already exists
      const { data: existing } = await supabase
        .from('asset_links')
        .select('id')
        .eq('organization_id', organizationId)
        .eq('parent_id', parentId)
        .eq('child_id', childId)
        .single();

      if (existing) {
        throw new Error('This link already exists');
      }

      // Check for circular references (child cannot be parent of parent)
      const { data: circular } = await supabase
        .from('asset_links')
        .select('id')
        .eq('organization_id', organizationId)
        .eq('parent_id', childId)
        .eq('child_id', parentId)
        .single();

      if (circular) {
        throw new Error('Cannot create circular link - this asset is already a parent of the selected asset');
      }

      const { error, data: newLink } = await supabase
        .from('asset_links')
        .insert([
          {
            organization_id: organizationId,
            parent_id: parentId,
            child_id: childId,
            should_group: shouldGroup,
          },
        ])
        .select()
        .single();

      if (error) throw error;
      return newLink;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-links'] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      notifications.show({
        title: 'Success',
        message: 'Asset linked successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to link asset',
        color: 'red',
      });
    },
  });
}

// Update an asset link (toggle should_group)
export function useUpdateAssetLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, shouldGroup }: { id: string; shouldGroup: boolean }) => {
      const { error, data: updatedLink } = await supabase
        .from('asset_links')
        .update({ should_group: shouldGroup })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return updatedLink;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-links'] });
      notifications.show({
        title: 'Success',
        message: 'Link updated successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update link',
        color: 'red',
      });
    },
  });
}

// Delete an asset link
export function useDeleteAssetLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('asset_links').delete().eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-links'] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      notifications.show({
        title: 'Success',
        message: 'Asset unlinked successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to unlink asset',
        color: 'red',
      });
    },
  });
}

// Get linked asset IDs that should be grouped (for checkout/checkin cascade)
export async function getGroupedChildAssetIds(
  organizationId: string,
  parentAssetId: string
): Promise<string[]> {
  const { data, error } = await supabase
    .from('asset_links')
    .select('child_id')
    .eq('organization_id', organizationId)
    .eq('parent_id', parentAssetId)
    .eq('should_group', true);

  if (error) throw error;
  return data.map((link) => link.child_id);
}
